"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ChatTurn, ToolCallEntry } from "@/hooks/client/usePlannerChat";
import GondolaLogo from "@/components/ui/svgComponents/GondolaLogo";
import { waitingActivityMessage } from "@/lib/planner/chatTurns";
import { cn } from "@/lib/utils";
import MarkdownContent, {
  type StreamingTextFadeRange,
} from "./MarkdownContent";
import ToolCallIndicator from "./ToolCallIndicator";
import TextWave from "./TextWave";
import ActivityTimeline from "./ActivityTimeline";
import { activityView } from "@/lib/planner/activityTimeline";
import { CheckCircle2 } from "lucide-react";
import { useSession } from "next-auth/react";
import type { Session } from "next-auth";

interface MessageThreadProps {
  /** The chat's sendMessage: rate retries on the latest turn, MCP App views on any turn. */
  onRetryRates?: (message: string) => void;
  turns: ChatTurn[];
  /**
   * Invoked when the user clicks the "Start a new chat" CTA that appears on
   * the per-session conversation-turns cap (GON-5359). Optional: when omitted,
   * the CTA is suppressed so callers without a fresh-chat affordance don't
   * render a dead button.
   */
  onNewChat?: () => void;
  isStreaming?: boolean;
  onStreamingTextReveal?: () => void;
}

export default function MessageThread({
  onRetryRates,
  turns,
  onNewChat,
  isStreaming,
  onStreamingTextReveal,
}: MessageThreadProps) {
  const session = useSession()?.data;
  const user = session?.user;
  const latestAssistantTurnId = [...turns]
    .reverse()
    .find((turn) => turn.role === "assistant")?.id;

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      {turns.map((turn) =>
        turn.role === "user" ? (
          <UserBubble key={turn.id} turn={turn} user={user} />
        ) : (
          <AssistantBubble
            key={turn.id}
            turn={turn}
            shouldSmoothReveal={
              Boolean(isStreaming && turn.id === latestAssistantTurnId) ||
              Boolean(turn.isStreaming)
            }
            onRetryRates={
              turn.id === latestAssistantTurnId ? onRetryRates : undefined
            }
            retryDisabled={isStreaming}
            onAppMessage={onRetryRates}
            onNewChat={onNewChat}
            onStreamingTextReveal={onStreamingTextReveal}
          />
        )
      )}
    </div>
  );
}

type UserIdentity = Pick<Session["user"], "name" | "email" | "image">;

function UserBubble({ turn, user }: { turn: ChatTurn; user?: UserIdentity }) {
  return (
    <div
      className="flex items-start justify-end gap-3"
      data-planner-role="user"
    >
      <div
        data-testid="planner-user-message-bubble"
        className="w-fit min-w-0 max-w-[82%] rounded-xl bg-muted px-4 py-3 md:max-w-xl md:rounded-lg md:px-3 md:py-2"
      >
        <p className="whitespace-pre-wrap break-words text-left text-base leading-6 text-foreground md:text-sm">
          {turn.content}
        </p>
      </div>
      <div className="hidden md:block">
        <UserAvatar user={user} />
      </div>
    </div>
  );
}

function UserAvatar({ user }: { user?: UserIdentity }) {
  if (user?.image) {
    return (
      <img
        data-testid="planner-user-avatar-image"
        src={user.image}
        alt=""
        width={32}
        height={32}
        className="h-8 w-8 shrink-0 rounded-full object-cover"
      />
    );
  }

  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
      <span
        data-testid="planner-user-avatar-initials"
        className="text-xs font-semibold text-primary"
      >
        {getUserInitials(user)}
      </span>
    </div>
  );
}

function getUserInitials(user?: UserIdentity): string {
  const nameParts = user?.name?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (nameParts.length >= 2) {
    return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
  }
  if (nameParts.length === 1) {
    return nameParts[0][0].toUpperCase();
  }
  const email = user?.email?.trim();
  if (email) return email[0].toUpperCase();
  return "U";
}

function AssistantBubble({
  onRetryRates,
  retryDisabled,
  onAppMessage,
  turn,
  shouldSmoothReveal,
  onNewChat,
  onStreamingTextReveal,
}: {
  turn: ChatTurn;
  onRetryRates?: (message: string) => void;
  retryDisabled?: boolean;
  onAppMessage?: (message: string) => void;
  shouldSmoothReveal?: boolean;
  onNewChat?: () => void;
  onStreamingTextReveal?: () => void;
}) {
  const view = activityView(turn);
  const answer = view.answer;
  const { displayedContent, fadeRanges } = useSmoothedStreamingText(
    answer,
    Boolean(shouldSmoothReveal && turn.content),
    onStreamingTextReveal
  );
  const showThinking =
    turn.status === "thinking" && !turn.content && !turn.toolCalls?.length;
  const thinkingElapsedMs = useStreamingElapsedMs(showThinking, turn.id);
  const activeToolCalls =
    turn.toolCalls?.filter((tc) => tc.status === "calling") ?? [];
  const completedToolCalls =
    turn.toolCalls?.filter((tc) => tc.status === "complete") ?? [];
  const showWritingIndicator =
    !!turn.isStreaming &&
    (!turn.content || !!turn.awaitingModelText) &&
    activeToolCalls.length === 0 &&
    completedToolCalls.length > 0;

  // GON-5359: only the per-session conversation-turns cap is non-recoverable
  // in-place — the user MUST start a new chat to continue. Other cap reasons
  // (tokens, iterations, rate_limited, loop_detected) and generic errors are
  // recoverable by retrying the same message, so they don't get the CTA.
  const showNewChatCta =
    turn.status === "error" && turn.errorReason === "turns" && !!onNewChat;
  const centerAssistantAvatar =
    !turn.content &&
    (showThinking || activeToolCalls.length > 0 || showWritingIndicator);

  return (
    <div
      data-testid="planner-assistant-row"
      className={cn(
        "flex gap-2 md:gap-3",
        centerAssistantAvatar ? "items-center" : "items-start"
      )}
      data-planner-role="assistant"
    >
      <AssistantAvatar />
      <div
        data-testid="planner-assistant-message-body"
        className="flex min-w-0 flex-1 flex-col gap-3"
      >
        {showThinking ? (
          <div
            data-testid="planner-assistant-status-row"
            className="flex min-h-8 items-center gap-2"
          >
            <TextWave
              text={waitingActivityMessage(
                turn.activityMessage,
                thinkingElapsedMs
              )}
            />
          </div>
        ) : null}

        {view.items.length ? (
          <ActivityTimeline
            toolLabel={(call) =>
              activeToolActivities([call], turn.toolCalls ?? [])[0]?.label ??
              "Checking your request"
            }
            turn={turn}
            onRetryRates={onRetryRates}
            retryDisabled={retryDisabled}
            onAppMessage={onAppMessage}
            appBusy={retryDisabled}
          />
        ) : (
          <>
            {activeToolActivities(activeToolCalls, turn.toolCalls ?? []).map(
              (activity) => (
                <div key={activity.key} className="flex flex-col gap-2">
                  <ToolCallIndicator
                    toolName={activity.toolName}
                    label={activity.label}
                    isLoading
                  />
                </div>
              )
            )}

            <ToolActivitySummary toolCalls={completedToolCalls} />
          </>
        )}

        {showWritingIndicator ? (
          <WritingAnswerIndicator toolCalls={completedToolCalls} />
        ) : null}

        {answer ? (
          <MarkdownContent
            content={displayedContent}
            fadeRanges={fadeRanges}
            showCaret={
              turn.isStreaming &&
              !turn.toolCalls?.some((tc) => tc.status === "calling")
            }
          />
        ) : null}

        {turn.status === "error" && turn.errorMessage ? (
          <p className="text-sm text-destructive">{turn.errorMessage}</p>
        ) : null}

        {showNewChatCta ? (
          <div>
            <Button variant="outline" size="sm" onClick={onNewChat}>
              Start a new chat
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function AssistantAvatar() {
  return (
    <div
      data-testid="planner-assistant-avatar-logo"
      role="img"
      aria-label="Gondola"
      className="flex h-8 w-8 shrink-0 items-center justify-center"
    >
      <GondolaLogo
        aria-hidden="true"
        focusable="false"
        color="hsl(var(--brand-gold))"
        className="h-7 w-7"
      />
    </div>
  );
}

function useSmoothedStreamingText(
  content: string | undefined,
  enabled: boolean,
  onReveal?: () => void
) {
  const target = content ?? "";
  const [displayState, setDisplayState] = useState(() => ({
    displayedContent: enabled ? "" : target,
    previousDisplayedContent: enabled ? "" : target,
    revealRevision: 0,
    fadeRanges: [] as StreamingTextFadeRange[],
    isRevealing: enabled,
  }));
  const displayedRef = useRef(enabled ? "" : target);
  const revealActiveRef = useRef(enabled);
  const timeoutRef = useRef<number | null>(null);
  const fadeFrameTimeoutRef = useRef<number | null>(null);
  const fadeRangesRef = useRef<StreamingTextFadeRange[]>([]);
  const nextFadeRangeIdRef = useRef(1);
  const onRevealRef = useRef(onReveal);

  const clearFadeRanges = useCallback(() => {
    if (fadeFrameTimeoutRef.current !== null) {
      window.clearTimeout(fadeFrameTimeoutRef.current);
      fadeFrameTimeoutRef.current = null;
    }
    fadeRangesRef.current = [];
  }, []);

  const scheduleFadeUpdate = useCallback(() => {
    if (fadeFrameTimeoutRef.current !== null) return;

    fadeFrameTimeoutRef.current = window.setTimeout(() => {
      fadeFrameTimeoutRef.current = null;

      if (fadeRangesRef.current.length === 0) return;

      const now = Date.now();
      const fadeRanges = fadeRangesRef.current.flatMap((range) => {
        const progress = Math.min(
          1,
          Math.max(
            0,
            (now - (range.startedAtMs ?? now)) / STREAMING_TEXT_FADE_MS
          )
        );
        if (progress >= 1) return [];

        return [
          {
            ...range,
            opacity:
              STREAMING_TEXT_FADE_INITIAL_OPACITY +
              (1 - STREAMING_TEXT_FADE_INITIAL_OPACITY) *
                easeOutCubic(progress),
          },
        ];
      });

      fadeRangesRef.current = fadeRanges;
      setDisplayState((prev) => ({ ...prev, fadeRanges }));

      if (fadeRanges.length > 0) {
        scheduleFadeUpdate();
      }
    }, STREAMING_TEXT_FADE_FRAME_MS);
  }, []);

  useEffect(() => {
    onRevealRef.current = onReveal;
  }, [onReveal]);

  useEffect(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (enabled) {
      revealActiveRef.current = true;
    }

    if (!enabled && !revealActiveRef.current) {
      clearFadeRanges();
      displayedRef.current = target;
      setDisplayState((prev) =>
        prev.displayedContent === target &&
        !prev.isRevealing &&
        prev.fadeRanges.length === 0
          ? prev
          : {
              displayedContent: target,
              previousDisplayedContent: target,
              revealRevision: prev.revealRevision,
              fadeRanges: [],
              isRevealing: false,
            }
      );
      return;
    }

    if (!target.startsWith(displayedRef.current)) {
      clearFadeRanges();
      displayedRef.current = "";
      setDisplayState((prev) => ({
        ...prev,
        displayedContent: "",
        previousDisplayedContent: "",
        fadeRanges: [],
      }));
    }

    const tick = () => {
      const remaining = target.length - displayedRef.current.length;
      if (remaining <= 0) {
        timeoutRef.current = null;
        if (!enabled) {
          revealActiveRef.current = false;
          setDisplayState((prev) => ({
            ...prev,
            isRevealing: false,
          }));
        }
        return;
      }

      const nextLength = nextStreamingTextLength(
        target,
        displayedRef.current.length
      );
      const previous = displayedRef.current;
      const next = target.slice(0, nextLength);
      const fadeRange = {
        id: nextFadeRangeIdRef.current,
        start: previous.length,
        end: next.length,
        opacity: STREAMING_TEXT_FADE_INITIAL_OPACITY,
        startedAtMs: Date.now(),
      };
      nextFadeRangeIdRef.current += 1;
      const fadeRanges = [...fadeRangesRef.current, fadeRange];
      fadeRangesRef.current = fadeRanges;
      displayedRef.current = next;
      setDisplayState((prev) => ({
        displayedContent: next,
        previousDisplayedContent: previous,
        revealRevision: prev.revealRevision + 1,
        fadeRanges,
        isRevealing: true,
      }));
      scheduleFadeUpdate();
      onRevealRef.current?.();
      timeoutRef.current = window.setTimeout(tick, STREAMING_TEXT_TICK_MS);
    };

    timeoutRef.current = window.setTimeout(tick, STREAMING_TEXT_TICK_MS);

    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [clearFadeRanges, enabled, scheduleFadeUpdate, target]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      clearFadeRanges();
    };
  }, [clearFadeRanges]);

  return enabled || displayState.isRevealing || displayState.fadeRanges.length
    ? displayState
    : {
        displayedContent: target,
        previousDisplayedContent: target,
        revealRevision: displayState.revealRevision,
        fadeRanges: [],
        isRevealing: false,
      };
}

const STREAMING_TEXT_TICK_MS = 110;
const STREAMING_TEXT_FADE_MS = 460;
const STREAMING_TEXT_FADE_FRAME_MS = 16;
const STREAMING_TEXT_FADE_INITIAL_OPACITY = 0.22;

function easeOutCubic(progress: number): number {
  return 1 - Math.pow(1 - progress, 3);
}

// How much text this reveal releases per tick.
//
// It sits *downstream* of the reveal in usePlannerChat, so the slower of the two is what a reader
// actually sees. One word per 110ms tick is roughly 50 chars/s, which was invisible while the hook
// above it trickled text in at 625 chars/s -- but that hook now drains at 2,500 chars/s and the
// backend delivers ~1,000 chars/s, so a fixed word per tick became the binding constraint and left
// a finished answer still typing itself out well after it had arrived.
//
// Below this much pending text, keep the original one-word-per-tick cadence: that is the part a
// reader is reading, and it is what the two reveal tests pin.
export const STREAMING_TEXT_WORD_BY_WORD_UNDER = 80;
// Fraction of a large backlog to clear per tick. A gentler divisor of 8 settles ~0.9s behind a
// 1,000 chars/s stream and still needs ~32 ticks to finish a 1,500-character answer after the
// text has all arrived; halving clears the same answer in 14. That matters because this reveal
// sits *downstream* of the one in usePlannerChat, so whichever of the two is slower is what the
// reader sees.
//
// The same pair of stacked reveals exists on mobile (hooks/usePlannerChat.ts feeding
// app/concierge.tsx), and its copy of this function is kept in step -- see the note there.
const STREAMING_TEXT_BACKLOG_DIVISOR = 2;

export function nextStreamingTextLength(
  target: string,
  currentLength: number
): number {
  const remaining = target.length - currentLength;
  if (remaining <= 0) return target.length;

  const minAdvance =
    remaining <= STREAMING_TEXT_WORD_BY_WORD_UNDER
      ? 1
      : Math.ceil(remaining / STREAMING_TEXT_BACKLOG_DIVISOR);

  let nextLength = currentLength;
  while (
    nextLength < target.length &&
    nextLength - currentLength < minAdvance
  ) {
    while (nextLength < target.length && /\s/.test(target[nextLength])) {
      nextLength += 1;
    }
    while (nextLength < target.length && !/\s/.test(target[nextLength])) {
      nextLength += 1;
    }
    while (nextLength < target.length && /\s/.test(target[nextLength])) {
      nextLength += 1;
    }
  }

  return Math.max(currentLength + 1, nextLength);
}

function useStreamingElapsedMs(active: boolean, resetKey: string): number {
  const [clock, setClock] = useState({
    resetKey,
    startedAtMs: 0,
    nowMs: 0,
  });

  useEffect(() => {
    if (!active) return;

    const startedAtMs = Date.now();
    const updateClock = () => {
      setClock({
        resetKey,
        startedAtMs,
        nowMs: Date.now(),
      });
    };
    const timeoutId = window.setTimeout(updateClock, 0);
    const intervalId = window.setInterval(() => {
      updateClock();
    }, 1_000);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, [active, resetKey]);

  if (!active || clock.resetKey !== resetKey) return 0;
  return Math.max(0, clock.nowMs - clock.startedAtMs);
}

function WritingAnswerIndicator({ toolCalls }: { toolCalls: ToolCallEntry[] }) {
  return (
    <div
      data-testid="planner-assistant-writing-row"
      className="flex min-h-8 items-center gap-2 text-sm text-muted-foreground"
    >
      <TextWave text={answeringActivityMessage(toolCalls)} />
    </div>
  );
}

interface ActiveToolActivity {
  key: string;
  toolName: string;
  label: string;
}

const ACTIVE_TOOL_LABELS: Record<string, string> = {
  search_hotels: "Searching live hotel rates",
  get_hotel_details: "Checking hotel details",
  get_hotel_rates: "Checking hotel rates",
  compare_rates: "Comparing cash and points",
  get_booking_link: "Checking booking details",
  book_hotel: "Processing your booking",
  get_payment_methods: "Checking payment options",
  get_rate_alerts: "Checking price monitoring",
  create_rate_alert: "Updating price monitoring",
  delete_rate_alert: "Updating price monitoring",
  get_upcoming_trips: "Reviewing your upcoming trips",
  get_past_trips: "Reviewing your travel history",
  get_loyalty_accounts: "Checking your loyalty accounts",
  get_free_night_credits: "Checking your free night certificates",
  get_favorite_hotels: "Checking your favorite hotels",
  predict_price: "Checking price timing",
  get_hotel_stats: "Checking price history",
  get_multi_night_rates: "Checking flexible-date rates",
  get_similar_hotels: "Finding similar hotels",
  optimize_loyalty_portfolio: "Reviewing your loyalty points",
  search_flights: "Checking flight options",
  search_vehicles: "Checking rental car options",
  search_places: "Checking local recommendations",
  update_traveler_profile: "Saving your preferences",
  get_traveler_snapshot: "Reviewing your traveler context",
  analyze_trip_needs: "Reviewing what your trip needs",
  get_chat_starters: "Finding personalized travel ideas",
  explain_hotel_fit: "Checking how each hotel fits your trip",
};

function activeToolActivities(
  activeCalls: ToolCallEntry[],
  allCalls: ToolCallEntry[]
): ActiveToolActivity[] {
  const callsByTool = new Map<string, ToolCallEntry[]>();
  for (const call of activeCalls) {
    callsByTool.set(call.name, [...(callsByTool.get(call.name) ?? []), call]);
  }
  const hotelNamesById = hotelNameIndex(allCalls);

  return Array.from(callsByTool, ([toolName, calls]) => {
    const names = hotelNamesForActiveCalls(calls, hotelNamesById);
    const baseLabel = ACTIVE_TOOL_LABELS[toolName] ?? "Checking your request";
    return {
      key: toolName,
      toolName,
      label:
        names.length > 0 && HOTEL_ENTITY_TOOLS.has(toolName)
          ? `${baseLabel} for ${formatActivityLabels(names)}`
          : baseLabel,
    };
  });
}

const HOTEL_ENTITY_TOOLS = new Set([
  "get_hotel_details",
  "get_hotel_rates",
  "compare_rates",
  "get_booking_link",
  "predict_price",
  "get_hotel_stats",
  "get_multi_night_rates",
  "get_similar_hotels",
  "explain_hotel_fit",
]);

function hotelNameIndex(toolCalls: ToolCallEntry[]): Map<string, string> {
  const index = new Map<string, string>();
  for (const call of toolCalls) {
    for (const record of nestedHotelRecords(call.widgetData)) {
      const id = hotelIdFromRecord(record);
      const name = hotelNameFromRecord(record);
      if (id && name) index.set(id, name);
    }
    for (const hotel of hotelsFromActivitySummary(call.activitySummary)) {
      index.set(hotel.id, hotel.name);
    }
  }
  return index;
}

function hotelsFromActivitySummary(
  summary?: string
): { id: string; name: string }[] {
  if (!summary) return [];
  return summary
    .split("\n")
    .map((line) => line.match(/^\s*-\s+(.+?)\s+\(ID:\s*([^)]+)\)/i))
    .filter((match): match is RegExpMatchArray => Boolean(match))
    .map((match) => ({
      id: match[2].trim(),
      name: cleanSummaryHotelName(match[1]),
    }));
}

function hotelNamesForActiveCalls(
  calls: ToolCallEntry[],
  hotelNamesById: Map<string, string>
): string[] {
  const names = calls.flatMap((call) => {
    const directNames = [
      ...hotelNamesFromRecord(call.args),
      ...hotelNamesFromRecord(call.widgetData),
    ];
    const resolvedName = hotelNamesById.get(hotelIdFromRecord(call.args) ?? "");
    return resolvedName ? [...directNames, resolvedName] : directNames;
  });
  return Array.from(new Set(names)).slice(0, 3);
}

function answeringActivityMessage(toolCalls: ToolCallEntry[]): string {
  const names = new Set(toolCalls.map((toolCall) => toolCall.name));
  if (["book_hotel", "get_booking_link"].some((name) => names.has(name))) {
    return "Preparing the next steps...";
  }
  if (
    [
      "search_hotels",
      "get_hotel_details",
      "get_hotel_rates",
      "compare_rates",
      "get_similar_hotels",
      "explain_hotel_fit",
    ].some((name) => names.has(name))
  ) {
    return "Ranking the best hotel options...";
  }
  if (names.has("search_flights")) {
    return "Narrowing down the best itineraries...";
  }
  if (names.has("search_vehicles")) {
    return "Choosing the best-value rental options...";
  }
  if (names.has("search_places")) {
    return "Bringing your recommendations together...";
  }
  if (
    [
      "get_loyalty_accounts",
      "get_free_night_credits",
      "optimize_loyalty_portfolio",
    ].some((name) => names.has(name))
  ) {
    return "Balancing points and cash value...";
  }
  return "Putting together your recommendation...";
}

const TOOL_ACTIVITY_LABELS: Record<string, string> = {
  search_hotels: "live hotel rates",
  get_hotel_details: "hotel details",
  get_hotel_rates: "hotel rates",
  compare_rates: "cash vs. points value",
  get_booking_link: "booking details",
  book_hotel: "booking details",
  get_payment_methods: "payment options",
  get_rate_alerts: "price monitoring",
  create_rate_alert: "price monitoring",
  delete_rate_alert: "price monitoring",
  get_upcoming_trips: "your trip context",
  get_past_trips: "your travel history",
  get_loyalty_accounts: "loyalty context",
  get_free_night_credits: "your free night certificates",
  get_favorite_hotels: "your favorite hotels",
  predict_price: "price timing",
  get_hotel_stats: "price history",
  get_multi_night_rates: "flexible-date rates",
  get_similar_hotels: "similar hotels",
  optimize_loyalty_portfolio: "loyalty redemption fit",
  search_flights: "flight options",
  search_vehicles: "rental car options",
  search_places: "local recommendations",
  update_traveler_profile: "your preferences",
  get_traveler_snapshot: "your traveler context",
  analyze_trip_needs: "your trip context",
  get_chat_starters: "personalized travel ideas",
  explain_hotel_fit: "hotel fit",
};

function ToolActivitySummary({ toolCalls }: { toolCalls: ToolCallEntry[] }) {
  const summary = toolActivitySentence(toolCalls);

  if (!summary) return null;

  return (
    <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-xs text-muted-foreground">{summary}</p>
      </div>
    </div>
  );
}

function toolActivitySentence(toolCalls: ToolCallEntry[]): string | null {
  const completedCalls = toolCalls.filter(
    (toolCall) => toolCall.status === "complete"
  );
  if (completedCalls.length === 0) return null;

  const checkedTargets: string[] = [];
  const actionPhrases: string[] = [];
  const outcomePhrases: string[] = [];
  const genericLabels = Array.from(
    new Set(
      completedCalls
        .filter(
          (toolCall) =>
            toolCall.name !== "compare_rates" &&
            toolCall.name !== "search_hotels" &&
            toolCall.name !== "get_hotel_details" &&
            toolCall.name !== "get_hotel_rates"
        )
        .map((toolCall) => TOOL_ACTIVITY_LABELS[toolCall.name])
        .filter((label): label is string => Boolean(label))
    )
  );
  checkedTargets.push(...genericLabels);

  const comparedHotelNames = hotelNamesForTool(completedCalls, "compare_rates");
  if (comparedHotelNames.length > 0) {
    checkedTargets.push(`rates at ${formatActivityLabels(comparedHotelNames)}`);
  } else if (
    completedCalls.some((toolCall) => toolCall.name === "compare_rates")
  ) {
    actionPhrases.push(
      `compared ${pluralizeHotels(hotelCountForTool(completedCalls, "compare_rates"))}`
    );
  }

  const hotelSearchOutcome = hotelSearchOutcomePhrase(completedCalls);
  if (hotelSearchOutcome) {
    outcomePhrases.push(hotelSearchOutcome);
  } else if (
    completedCalls.some((toolCall) => toolCall.name === "search_hotels")
  ) {
    checkedTargets.push("live hotel rates");
  }

  const detailedHotelNames = hotelNamesForTool(
    completedCalls,
    "get_hotel_details"
  );
  if (detailedHotelNames.length > 0) {
    checkedTargets.push(
      `details for ${formatActivityLabels(detailedHotelNames)}`
    );
  } else if (
    completedCalls.some((toolCall) => toolCall.name === "get_hotel_details")
  ) {
    checkedTargets.push("hotel details");
  }

  const ratedHotelNames = hotelNamesForTool(completedCalls, "get_hotel_rates");
  if (ratedHotelNames.length > 0) {
    checkedTargets.push(`rates for ${formatActivityLabels(ratedHotelNames)}`);
  } else if (
    completedCalls.some((toolCall) => toolCall.name === "get_hotel_rates")
  ) {
    checkedTargets.push("hotel rates");
  }

  const phrases = [
    ...(checkedTargets.length > 0
      ? [`checked ${formatActivityLabels(checkedTargets)}`]
      : []),
    ...outcomePhrases,
    ...actionPhrases,
  ];
  if (phrases.length === 0) return null;
  return `Gondola ${formatActivityLabels(phrases)}.`;
}

function hotelSearchOutcomePhrase(toolCalls: ToolCallEntry[]): string | null {
  const searchCalls = toolCalls.filter(
    (toolCall) => toolCall.name === "search_hotels"
  );
  if (searchCalls.length === 0) return null;

  const names = hotelNamesForTool(searchCalls, "search_hotels");
  const count = Math.max(
    0,
    ...searchCalls.map((call) =>
      Math.max(
        hotelCountFromRecord(call.widgetData),
        hotelSearchContextFromSummary(call.activitySummary).count
      )
    )
  );
  const location = searchCalls
    .map(
      (call) =>
        stringField(call.widgetData, "location") ??
        hotelSearchContextFromSummary(call.activitySummary).location
    )
    .find(Boolean);
  const locationPhrase = location ? ` near ${location}` : "";

  if (count > names.length && count > 1) {
    const examples =
      names.length > 0 ? `, including ${formatActivityLabels(names)}` : "";
    return `found ${count} hotels${locationPhrase}${examples}`;
  }
  if (names.length > 0) {
    return `found live rates at ${formatActivityLabels(names)}`;
  }
  if (count > 0) {
    return `found ${pluralizeHotels(count)}${locationPhrase}`;
  }
  return null;
}

function stringField(
  record: Record<string, unknown> | undefined,
  key: string
): string | null {
  const value = record?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function hotelSearchContextFromSummary(summary?: string): {
  count: number;
  location: string | null;
} {
  const match = summary?.match(
    /Found\s+(\d+)\s+hotels?\s+near\s+(.+?)\s+for\s+\d{4}-\d{2}-\d{2}/i
  );
  return {
    count: match ? Number(match[1]) : 0,
    location: match?.[2]?.trim() || null,
  };
}

function hotelNamesForTool(
  toolCalls: ToolCallEntry[],
  toolName: string
): string[] {
  const names = toolCalls
    .filter((toolCall) => toolCall.name === toolName)
    .flatMap((toolCall) => [
      ...hotelNamesFromRecord(toolCall.widgetData),
      ...hotelNamesFromRecord(toolCall.args),
      ...hotelNamesFromActivitySummary(toolCall.activitySummary),
    ]);
  return Array.from(new Set(names)).slice(0, 3);
}

function hotelNamesFromRecord(record?: Record<string, unknown>): string[] {
  if (!record) return [];

  const directName = hotelNameFromRecord(record);
  const directNames = directName ? [directName] : [];
  const nestedNames = nestedHotelRecords(record)
    .map(hotelNameFromRecord)
    .filter((name): name is string => Boolean(name));
  return [...directNames, ...nestedNames];
}

function nestedHotelRecords(
  record?: Record<string, unknown>
): Record<string, unknown>[] {
  if (!record) return [];
  const hotel = record.hotel;
  const hotels = record.hotels ?? record.hotel_options ?? record.hotelOptions;
  const records =
    hotel && typeof hotel === "object"
      ? [hotel as Record<string, unknown>]
      : [];
  if (!Array.isArray(hotels)) return records;
  return [
    ...records,
    ...hotels.filter(
      (hotel): hotel is Record<string, unknown> =>
        Boolean(hotel) && typeof hotel === "object"
    ),
  ];
}

function hotelNameFromRecord(record?: Record<string, unknown>): string | null {
  if (!record) return null;
  const candidate =
    record.hotel_name ??
    record.hotelName ??
    record.name ??
    record.property_name ??
    record.propertyName;
  return typeof candidate === "string" && candidate.trim()
    ? candidate.trim()
    : null;
}

function hotelIdFromRecord(record?: Record<string, unknown>): string | null {
  if (!record) return null;
  const candidate =
    record.hotel_id ??
    record.hotelId ??
    record.vervotech_property_id ??
    record.vervotechPropertyId ??
    record.id;
  return typeof candidate === "string" || typeof candidate === "number"
    ? String(candidate)
    : null;
}

function hotelNamesFromActivitySummary(summary?: string): string[] {
  const text = summary?.trim();
  if (!text) return [];

  const tableNames = text
    .split("\n")
    .filter((line) => line.trim().startsWith("|"))
    .map((line) => line.split("|")[1]?.trim())
    .filter(
      (candidate): candidate is string =>
        Boolean(candidate) &&
        !/^hotel$/i.test(candidate) &&
        !/^-+$/.test(candidate)
    )
    .map(cleanSummaryHotelName)
    .filter((name): name is string => Boolean(name));
  if (tableNames.length > 0) return tableNames;

  const bulletNames = hotelsFromActivitySummary(text).map(
    (hotel) => hotel.name
  );
  if (bulletNames.length > 0) return bulletNames;

  const detailsMatch = text.match(/^(.+?)\.\s+Address:/i);
  const candidate = detailsMatch?.[1]?.trim();
  return candidate ? [cleanSummaryHotelName(candidate)].filter(Boolean) : [];
}

function cleanSummaryHotelName(name: string): string {
  return name.replace(/\*\*/g, "").replace(/`/g, "").trim();
}

function hotelCountForTool(
  toolCalls: ToolCallEntry[],
  toolName: string
): number {
  const counts = toolCalls
    .filter((toolCall) => toolCall.name === toolName)
    .map((toolCall) => {
      return Math.max(
        hotelCountFromRecord(toolCall.widgetData),
        hotelCountFromRecord(toolCall.args),
        hotelNamesFromActivitySummary(toolCall.activitySummary).length
      );
    });
  return Math.max(0, ...counts);
}

function hotelCountFromRecord(record?: Record<string, unknown>): number {
  if (!record) return 0;
  const count = record.count ?? record.hotel_count ?? record.hotelCount;
  if (typeof count === "number" && Number.isFinite(count) && count > 0) {
    return Math.floor(count);
  }
  const hotels = record.hotels ?? record.hotel_options ?? record.hotelOptions;
  return Array.isArray(hotels) ? hotels.length : 0;
}

function pluralizeHotels(count: number): string {
  if (count <= 0) return "hotel rates";
  return `${count} ${count === 1 ? "hotel" : "hotels"}`;
}

function formatActivityLabels(labels: string[]): string {
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}
